import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { ChildrenTableProps, MappedChildRow, DirectChildWip } from "../../types/traceability";
import { kgFmt, pcsFmt, sum } from "../../shared/formatting";

// Map direct children for table display
function mapDirectChildren(children: DirectChildWip[]): MappedChildRow[] {
  return (children || []).map((child, idx) => ({
    key: `child:${child.child_wip_id}:${idx}`,
    child_wip_id: child.child_wip_id,
    child_wip_lot_number: child.child_wip_lot_number,
    used_kg: child.used_kg,
    used_pcs: child.used_pcs,
    used_lot_number: child.used_lot_number,
    child_total_produced_kg: child.child_total_produced_kg,
    child_total_produced_pcs: child.child_total_produced_pcs,
    children: null, // For lazy loading - will be populated on expand
  }));
}

// Map deeper children (for recursive expansion)
function mapDeeperChildren(children: DirectChildWip[]): MappedChildRow[] {
  return (children || []).map((child, idx) => ({
    key: `deep:${child.child_wip_id}:${idx}`,
    child_wip_id: child.child_wip_id,
    child_wip_lot_number: child.child_wip_lot_number,
    used_kg: child.used_kg,
    used_pcs: child.used_pcs,
    used_lot_number: child.used_lot_number,
    child_total_produced_kg: child.child_total_produced_kg,
    child_total_produced_pcs: child.child_total_produced_pcs,
    children: null,
  }));
}

export default function ChildrenTable({
  level,
  rows,
  fetchChildren,
  loading = false,
  error = null,
}: ChildrenTableProps) {
  const [data, setData] = useState<MappedChildRow[]>(rows || []);
  const [expandedRows, setExpandedRows] = useState({});
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());

  const totals = useMemo(
    () => ({
      usedKg: sum(data, (r) => r.used_kg),
      usedPcs: sum(data, (r) => r.used_pcs),
      producedKg: sum(data, (r) => r.child_total_produced_kg),
      producedPcs: sum(data, (r) => r.child_total_produced_pcs),
    }),
    [data]
  );

  useEffect(() => setData(rows || []), [rows]);

  const onRowExpand = async (e: any) => {
    const row: MappedChildRow = e.data;
    if (row.children == null && row.child_wip_id) {
      setLoadingRows((prev) => new Set(prev).add(row.child_wip_id));
      try {
        const res = await fetchChildren(row.child_wip_id);
        if (res?.ok && res.children) {
          const children = mapDeeperChildren(res.children);
          setData((prev) =>
            prev.map((r) => (r.key === row.key ? { ...r, children } : r))
          );
        }
      } finally {
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(row.child_wip_id);
          return next;
        });
      }
    }
  };

  const rowExpansionTemplate = (row: MappedChildRow) => {
    const isLoading = loadingRows.has(row.child_wip_id);
    return (
      <div className="px-2 py-3 space-y-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Children of {row.child_wip_lot_number}
          </div>
          {isLoading ? (
            <div className="text-sm text-gray-500 py-4">Loading...</div>
          ) : (
            <ChildrenTable
              level={level + 1}
              rows={row.children || []}
              fetchChildren={fetchChildren}
              loading={loading}
              error={error}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <DataTable
      value={data}
      dataKey="key"
      responsiveLayout="scroll"
      size="small"
      className="p-datatable-sm"
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data)}
      onRowExpand={onRowExpand}
      rowExpansionTemplate={rowExpansionTemplate}
      emptyMessage={
        loading ? "Loading..." : error ? error : "No children found"
      }
      footer={
        data.length > 0 ? (
          <div className="flex justify-end gap-6 text-sm">
            <div>
              <span className="text-gray-600">Used (kg): </span>
              <span className="font-semibold">{kgFmt(totals.usedKg)}</span>
            </div>
            <div>
              <span className="text-gray-600">Used (pcs): </span>
              <span className="font-semibold">{pcsFmt(totals.usedPcs)}</span>
            </div>
            <div>
              <span className="text-gray-600">Produced (kg): </span>
              <span className="font-semibold">{kgFmt(totals.producedKg)}</span>
            </div>
            <div>
              <span className="text-gray-600">Produced (pcs): </span>
              <span className="font-semibold">{pcsFmt(totals.producedPcs)}</span>
            </div>
          </div>
        ) : null
      }
    >
      <Column expander style={{ width: "3rem" }} />
      <Column
        header="Child WIP Lot Number"
        body={(r: MappedChildRow) => (
          <div className="font-medium text-gray-900">
            {r.child_wip_lot_number}
          </div>
        )}
      />
      <Column
        header="Used (kg)"
        body={(r: MappedChildRow) => kgFmt(r.used_kg)}
        style={{ minWidth: 140 }}
      />
      <Column
        header="Used (pcs)"
        body={(r: MappedChildRow) => pcsFmt(r.used_pcs)}
        style={{ minWidth: 120 }}
      />
      <Column
        header="Produced (kg)"
        body={(r: MappedChildRow) => kgFmt(r.child_total_produced_kg)}
        style={{ minWidth: 180 }}
      />
      <Column
        header="Produced (pcs)"
        body={(r: MappedChildRow) => pcsFmt(r.child_total_produced_pcs)}
        style={{ minWidth: 180 }}
      />
      <Column
        header="View"
        body={(r: MappedChildRow) => {
          if (r.child_wip_id) {
            return (
              <button
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                onClick={() => window.open(`/wips/${r.child_wip_id}`, "_blank")}
              >
                View
              </button>
            );
          }
          return <span className="text-gray-400 text-sm">—</span>;
        }}
        style={{ minWidth: 120 }}
      />
    </DataTable>
  );
}

// Export mapping functions for use in parent component
export { mapDirectChildren, mapDeeperChildren };

